'use client';

import React from 'react';
import clsx from 'clsx';

export type StatusBannerVariant = 'info' | 'success' | 'warning' | 'error';

export interface StatusBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Banner variant determines the color scheme and icon
   */
  variant: StatusBannerVariant;

  /**
   * Title text for the banner
   */
  title?: string;

  /**
   * Message or description text
   */
  message: React.ReactNode;

  /**
   * If true, shows a close button
   */
  dismissible?: boolean;

  /**
   * Callback when close button is clicked
   */
  onDismiss?: () => void;

  /**
   * Custom icon to override the default variant icon
   */
  icon?: React.ReactNode;

  /**
   * Action buttons or links to display
   */
  actions?: React.ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;
}

/**
 * StatusBanner Component
 *
 * A reusable banner component for displaying status messages, alerts, and notifications.
 * Supports multiple variants (info, success, warning, error) with appropriate styling.
 *
 * @example
 * ```tsx
 * // Success banner
 * <StatusBanner
 *   variant="success"
 *   title="Job Completed"
 *   message="All inspections have been successfully completed."
 * />
 *
 * // Warning banner with action
 * <StatusBanner
 *   variant="warning"
 *   title="Incomplete Data"
 *   message="Some required fields are missing."
 *   actions={<button>Complete Now</button>}
 *   dismissible
 *   onDismiss={() => setShowBanner(false)}
 * />
 *
 * // Error banner
 * <StatusBanner
 *   variant="error"
 *   message="Failed to save changes. Please try again."
 * />
 * ```
 */
export function StatusBanner({
  variant,
  title,
  message,
  dismissible = false,
  onDismiss,
  icon,
  actions,
  className,
  ...rest
}: StatusBannerProps) {
  const [isVisible, setIsVisible] = React.useState(true);

  // Handle dismiss
  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  // Don't render if dismissed
  if (!isVisible) {
    return null;
  }

  // Variant-specific styling
  const variantStyles = {
    info: {
      container: 'bg-blue-50 border-blue-200',
      icon: 'text-blue-400',
      title: 'text-blue-900',
      message: 'text-blue-700',
      closeButton: 'text-blue-500 hover:text-blue-700 focus:ring-blue-500',
    },
    success: {
      container: 'bg-green-50 border-green-200',
      icon: 'text-green-400',
      title: 'text-green-900',
      message: 'text-green-700',
      closeButton: 'text-green-500 hover:text-green-700 focus:ring-green-500',
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-200',
      icon: 'text-yellow-400',
      title: 'text-yellow-900',
      message: 'text-yellow-700',
      closeButton: 'text-yellow-500 hover:text-yellow-700 focus:ring-yellow-500',
    },
    error: {
      container: 'bg-red-50 border-red-200',
      icon: 'text-red-400',
      title: 'text-red-900',
      message: 'text-red-700',
      closeButton: 'text-red-500 hover:text-red-700 focus:ring-red-500',
    },
  };

  const styles = variantStyles[variant];

  // Default icons for each variant
  const defaultIcons = {
    info: (
      <svg
        className="h-5 w-5"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
    success: (
      <svg
        className="h-5 w-5"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg
        className="h-5 w-5"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg
        className="h-5 w-5"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  return (
    <div
      className={clsx(
        'rounded-lg border px-4 py-3 shadow-sm',
        styles.container,
        className
      )}
      role="alert"
      aria-live="polite"
      {...rest}
    >
      <div className="flex items-start">
        {/* Icon */}
        <div className={clsx('flex-shrink-0', styles.icon)}>
          {icon || defaultIcons[variant]}
        </div>

        {/* Content */}
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={clsx('text-sm font-semibold', styles.title)}>
              {title}
            </h3>
          )}
          <div
            className={clsx('text-sm', styles.message, title && 'mt-1')}
          >
            {message}
          </div>

          {/* Actions */}
          {actions && (
            <div className="mt-3 flex items-center gap-3">{actions}</div>
          )}
        </div>

        {/* Dismiss Button */}
        {dismissible && (
          <div className="ml-3 flex-shrink-0">
            <button
              type="button"
              onClick={handleDismiss}
              className={clsx(
                'inline-flex rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
                styles.closeButton
              )}
              aria-label="Dismiss"
            >
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StatusBanner;
